import { useAppContext } from "../../../AppContext";
import { DependencyList, useEffect, useRef } from "react";
import { IconType } from "../../icons/Icon";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "./usePermissions";

interface IAdditionalLayoutButtonProps {
  iconType: IconType;
  href?: string;
  rawPath?: string;
  dependencies?: DependencyList;
  validator?: boolean;
  buttonOnClick?: () => void;
}

export const useAddAdditionalLayoutButton = ({
  iconType,
  href,
  rawPath,
  dependencies = [],
  validator = true,
  buttonOnClick,
}: IAdditionalLayoutButtonProps) => {
  const {
    setAdditionalLayoutButtonOnClick,
    setAdditionalLayoutButtonIconType,
  } = useAppContext();

  const { userHavePermissionsByRoute, userHavePermissions } = usePermissions();
  const navigate = useNavigate();

  const previousPermissionRef = useRef<boolean | null>(null);

  const route = rawPath ?? href;
  let currentPermission = false;

  if (validator) {
    if (route) {
      try {
        currentPermission =
          userHavePermissionsByRoute(route) || userHavePermissions(route);
      } catch {
        currentPermission = false;
      }
    } else if (buttonOnClick) {
      currentPermission = true;
    }
  }

  useEffect(() => {
    if (!validator || !currentPermission) {
      setAdditionalLayoutButtonOnClick(() => () => {});
      setAdditionalLayoutButtonIconType(undefined);
      return;
    }

    const clickHandler = () => {
      if (buttonOnClick) {
        buttonOnClick();
      } else {
        navigate(href ?? "");
      }
    };

    setAdditionalLayoutButtonOnClick(() => clickHandler);
    setAdditionalLayoutButtonIconType(iconType);

    // Cleanup function to remove the button when component unmounts
    return () => {
      setAdditionalLayoutButtonOnClick(() => () => {});
      setAdditionalLayoutButtonIconType(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPermission, validator, iconType, href, buttonOnClick, navigate, setAdditionalLayoutButtonOnClick, setAdditionalLayoutButtonIconType, ...dependencies]);

  return { setAdditionalLayoutButtonIconType };
};
